"use client"

import { useEffect, useRef, useState } from "react"
import type { FaqDto } from "../types"
import { fetchFaqListAction } from "../api/faqActions"

type UseFaqListArgs = {
  position: string
  locale: string
  initialFaqs: FaqDto[]
  initialAvailableLocales: string[]
}

/**
 * Giữ initialFaqs (đã lọc sẵn ở SSR) cho lần render đầu; gọi lại fetchFaqListAction
 * khi đổi locale HOẶC khi position đổi (chuyển trang client-side trong cùng layout —
 * layout không tự re-render nên FaqSection phải tự refetch, xem FaqSection.tsx). Nếu
 * position đổi, xoá faqs cũ ngay lập tức để không hiện nhầm câu hỏi của trang vừa
 * rời đi trong lúc chờ fetch mới.
 */
export function useFaqList({ position, locale, initialFaqs, initialAvailableLocales }: UseFaqListArgs) {
  const [faqs, setFaqs] = useState<FaqDto[]>(initialFaqs)
  const [availableLocales, setAvailableLocales] = useState<string[]>(initialAvailableLocales)
  const [loading, setLoading] = useState(false)
  const isFirstRender = useRef(true)
  const prevPosition = useRef(position)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      prevPosition.current = position
      return
    }

    const positionChanged = prevPosition.current !== position
    prevPosition.current = position

    let cancelled = false
    setLoading(true)
    if (positionChanged) {
      setFaqs([])
    }

    fetchFaqListAction(position, locale)
      .then((data) => {
        if (cancelled) return
        setFaqs(data.faqs)
        setAvailableLocales(data.availableLocales)
      })
      .catch(() => {
        if (!cancelled) {
          setFaqs([])
          setAvailableLocales([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [locale, position])

  return { faqs, loading, availableLocales }
}
